import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { formatCurrency, getActiveCompanyCurrency } from "@/lib/currencies/currenciesStore";
import {
  PaymentMethodFieldsGroup,
  emptyPaymentMethodDraft,
  type PaymentMethodDraft,
} from "@/components/sales/PaymentMethodFields";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { pkrAmountShortLabel } from "@/lib/formatPkrAmount";
import { fetchInvestorBalance, productPaymentErrorMessage } from "@/lib/catalog/productPaymentsStore";
import { format } from "date-fns";
import { Loader2, HandCoins, Calculator, Info, FileText } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

type FundingSource = "cash_bank" | "investor_balance";
type PayoutSettlementMode = "cash_pay" | "reinvest";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productId: string | number;
  outstanding: number;
  action: "pay" | "receive";
  partyName: string;
  partyId: number;
  transactionType: string;
  scheduledPaymentId: number;
  productInvoiceId?: number | null;
  currencyCode?: string | null;
  currencyAmount?: number | null;
  exchangeRate?: number | null;
  /** Receivable/payable line shown in the right-side payment summary */
  lineLabel?: string | null;
  /** Invoice header info shown in dialog right panel */
  invoiceNo?: string | null;
  invoiceDate?: string | null;
  invoiceDueDate?: string | null;
  /** Itemized cost lines linked to this invoice */
  invoiceCostItems?: Array<{
    label: string;
    currencyAmount: number | null;
    currencyCode: string | null;
    amount: number;
  }>;
  acquisitionType?: "purchased" | "consignment" | null;
  /** When false, amount cannot exceed outstanding (base purchase). */
  allowExcess?: boolean;
  /** When set on investor/partner payout, enables Cash Pay vs Reinvest in this same dialog. */
  profitDistributionId?: number | null;
  /**
   * When settling an open profit share:
   * - true (default): Cash / Bank + Reinvest (product view)
   * - false: Reinvest only; cash later from reserved (investors page)
   */
  allowCashFromPayable?: boolean;
  /** When set, dialog is editing an existing History settlement. */
  editingPaymentId?: number | null;
  initialAmount?: number | null;
  initialPaymentDate?: string | null;
  initialNotes?: string | null;
  initialPaymentMethod?: string | null;
  initialBankId?: number | null;
  initialTransactionReferenceNo?: string | null;
  initialPayOrderNo?: string | null;
  initialChequeNo?: string | null;
  initialFundingSource?: FundingSource | null;
  onSubmit: (payload: {
    amount: number;
    payment_date: string;
    payment_method: string | null;
    funding_source?: FundingSource;
    bank_id: number | null;
    bank_name: string | null;
    transaction_reference_no: string | null;
    pay_order_no: string | null;
    cheque_no?: string | null;
    deposit_slip_no?: string | null;
    notes: string | null;
    action: "pay" | "receive";
    party_id: number;
    transaction_type?: string | null;
    scheduled_payment_id?: number | null;
    product_invoice_id?: number | null;
    currency_code: string | null;
    currency_amount: number | null;
    exchange_rate: number | null;
    settlement_mode?: PayoutSettlementMode;
    profit_distribution_id?: number | null;
    destination_product_id?: number | null;
    destination_description?: string | null;
  }) => Promise<void>;
};

function todayIso(): string {
  return format(new Date(), "yyyy-MM-dd");
}

function parseAmount(raw: string): number | null {
  const trimmed = raw.trim().replace(/,/g, "");
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100) / 100;
}

export function ProductPaymentDialog({
  open,
  onOpenChange,
  productId,
  outstanding,
  action,
  partyName,
  partyId,
  transactionType,
  scheduledPaymentId,
  productInvoiceId,
  currencyCode: initialCurrencyCode,
  currencyAmount: initialCurrencyAmount,
  exchangeRate: initialExchangeRate,
  lineLabel = null,
  invoiceNo,
  invoiceDate,
  invoiceDueDate,
  invoiceCostItems,
  acquisitionType = null,
  allowExcess = true,
  profitDistributionId = null,
  allowCashFromPayable = true,
  editingPaymentId = null,
  initialAmount = null,
  initialPaymentDate = null,
  initialNotes = null,
  initialPaymentMethod = null,
  initialBankId = null,
  initialTransactionReferenceNo = null,
  initialPayOrderNo = null,
  initialChequeNo = null,
  initialFundingSource = null,
  onSubmit,
}: Props) {
  const [amountInput, setAmountInput] = useState("");
  const [paymentDate, setPaymentDate] = useState(todayIso());
  const [methodDraft, setMethodDraft] = useState<PaymentMethodDraft>(emptyPaymentMethodDraft());
  const [fundingSource, setFundingSource] = useState<FundingSource>("cash_bank");
  const [investorBalance, setInvestorBalance] = useState<number | null>(null);
  const [investorBalanceLoading, setInvestorBalanceLoading] = useState(false);
  const [settlementMode, setSettlementMode] = useState<PayoutSettlementMode>("cash_pay");
  const [destinationDescription, setDestinationDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [currencyCode, setCurrencyCode] = useState("");
  const [currencyAmountInput, setCurrencyAmountInput] = useState("");
  const [exchangeRateInput, setExchangeRateInput] = useState("1.0000");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditing = editingPaymentId != null && Number(editingPaymentId) > 0;
  const isPay = action === "pay";
  const isPartnerOrInvestor =
    transactionType === "deal_partner" ||
    transactionType === "partner" ||
    transactionType === "investment";
  const isConsignment = acquisitionType === "consignment";
  // Balance adjust (no cash) is receive-only against existing 220401/220301 payable credit.
  const showFundingSource = isPartnerOrInvestor && !isPay;
  const showPayoutSettlementMode =
    !isEditing
    && isPartnerOrInvestor
    && isPay
    && profitDistributionId != null
    && Number(profitDistributionId) > 0;
  const reinvestOnly = showPayoutSettlementMode && !allowCashFromPayable;
  const useReinvest =
    showPayoutSettlementMode && (reinvestOnly || settlementMode === "reinvest");
  const useInvestorBalance = showFundingSource && fundingSource === "investor_balance";
  const skipPaymentMethod = useInvestorBalance || useReinvest;

  useEffect(() => {
    if (!open) return;
    const editAmount = initialAmount != null && Number(initialAmount) > 0
      ? Math.round(Number(initialAmount) * 100) / 100
      : null;
    setAmountInput(
      editAmount != null
        ? String(editAmount)
        : (outstanding > 0 ? String(outstanding) : ""),
    );
    setPaymentDate(initialPaymentDate?.trim() || todayIso());
    setMethodDraft(emptyPaymentMethodDraft({
      paymentMethod: (initialPaymentMethod as PaymentMethodDraft["paymentMethod"]) || "",
      bankId: initialBankId != null && Number(initialBankId) > 0 ? String(initialBankId) : "",
      transactionReferenceNo: initialTransactionReferenceNo?.trim() || "",
      payOrderNo: initialPayOrderNo?.trim() || "",
      chequeNo: initialChequeNo?.trim() || "",
    }));
    setFundingSource(initialFundingSource === "investor_balance" ? "investor_balance" : "cash_bank");
    setSettlementMode(allowCashFromPayable ? "cash_pay" : "reinvest");
    setDestinationDescription("");
    setInvestorBalance(null);
    setNotes(initialNotes?.trim() || "");
    setCurrencyCode(initialCurrencyCode || "");
    setCurrencyAmountInput(initialCurrencyAmount ? String(initialCurrencyAmount) : "");
    setExchangeRateInput(initialExchangeRate ? String(initialExchangeRate) : "1.0000");
    setSaving(false);
    setError(null);
  }, [
    open,
    outstanding,
    initialCurrencyCode,
    initialCurrencyAmount,
    initialExchangeRate,
    profitDistributionId,
    allowCashFromPayable,
    editingPaymentId,
    initialAmount,
    initialPaymentDate,
    initialNotes,
    initialPaymentMethod,
    initialBankId,
    initialTransactionReferenceNo,
    initialPayOrderNo,
    initialChequeNo,
    initialFundingSource,
  ]);

  const applyAmountFromPayable = (bal: number) => {
    const fill = outstanding > 0.009 ? Math.min(Math.max(0, bal), outstanding) : Math.max(0, bal);
    const rounded = Math.round(fill * 100) / 100;
    setAmountInput(rounded > 0.009 ? String(rounded) : "");
    if (currencyCode && currencyCode !== getActiveCompanyCurrency().code) {
      const r = Number(exchangeRateInput) || 1;
      setCurrencyAmountInput(
        rounded > 0.009 && r > 0 ? String(Math.round((rounded / r) * 100) / 100) : "",
      );
    }
  };

  useEffect(() => {
    // Load investor payable only when that funding source is selected.
    if (!open || !showFundingSource || !partyId || fundingSource !== "investor_balance") {
      if (fundingSource !== "investor_balance") setInvestorBalance(null);
      return;
    }
    let cancelled = false;
    setInvestorBalanceLoading(true);
    void fetchInvestorBalance(productId, partyId)
      .then((row) => {
        if (cancelled) return;
        const bal = Math.round(Number(row.balance ?? 0) * 100) / 100;
        // When editing, available balance excludes this payment's prior apply — add it back for caps.
        const editAmt = isEditing && initialAmount != null ? Math.max(0, Number(initialAmount)) : 0;
        setInvestorBalance(Math.round((bal + editAmt) * 100) / 100);
        if (!isEditing) {
          applyAmountFromPayable(bal);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setInvestorBalance(null);
          if (!isEditing) setAmountInput("");
        }
      })
      .finally(() => {
        if (!cancelled) setInvestorBalanceLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fill amount only when funding source / party changes
  }, [open, showFundingSource, productId, partyId, fundingSource, isEditing, initialAmount]);

  const handleCurrencyChange = (newCode: string) => {
    setCurrencyCode(newCode);
    if (!newCode || newCode === getActiveCompanyCurrency().code) {
      setExchangeRateInput("1.0000");
      setCurrencyAmountInput("");
    } else {
      const amount = parseAmount(amountInput) ?? 0;
      const rate = Number(exchangeRateInput) || 1;
      setCurrencyAmountInput(amount > 0 ? String(Math.round((amount / rate) * 100) / 100) : "");
    }
  };

  const handleForeignAmountChange = (val: string) => {
    setCurrencyAmountInput(val);
    const amt = Number(val);
    const rate = Number(exchangeRateInput);
    if (Number.isFinite(amt) && Number.isFinite(rate) && rate > 0) {
      const pkr = Math.round(amt * rate * 100) / 100;
      setAmountInput(pkr > 0 ? String(pkr) : "");
    }
  };

  const handlePkrAmountChange = (val: string) => {
    setAmountInput(val);
    const pkr = parseAmount(val) ?? 0;
    const rate = Number(exchangeRateInput);
    if (currencyCode && currencyCode !== getActiveCompanyCurrency().code && rate > 0) {
      setCurrencyAmountInput(pkr > 0 ? String(Math.round((pkr / rate) * 100) / 100) : "");
    }
  };

  const hasForeign = Boolean(currencyCode && currencyCode !== getActiveCompanyCurrency().code);
  const rate = Number(exchangeRateInput) || 1.0;
  const expectedForeign = initialCurrencyAmount || (outstanding / rate);
  const enteredForeign = Number(currencyAmountInput) || 0;
  const remainingForeign = Math.max(0, expectedForeign - enteredForeign);

  const enteredAmount = parseAmount(amountInput) ?? 0;
  const appliedAmount = outstanding > 0 ? Math.min(enteredAmount, outstanding) : enteredAmount;
  const excessAmount = allowExcess
    ? Math.max(0, Math.round((enteredAmount - appliedAmount) * 100) / 100)
    : 0;
  const remaining = Math.max(0, outstanding - (allowExcess ? appliedAmount : Math.min(enteredAmount, outstanding)));
  const paidPercent = outstanding > 0
    ? Math.min(100, Math.round(((allowExcess ? appliedAmount : Math.min(enteredAmount, outstanding)) / outstanding) * 100))
    : 0;
  const enteredAmountLabel = pkrAmountShortLabel(enteredAmount);
  const investorCapitalAvailable = useInvestorBalance && investorBalance != null
    ? Math.max(0, Math.round(investorBalance * 100) / 100)
    : null;
  const investorCapitalRemaining = investorCapitalAvailable != null
    ? Math.max(0, Math.round((investorCapitalAvailable - enteredAmount) * 100) / 100)
    : null;

  const applyPreset = (percent: number) => {
    const value = Math.round((outstanding * (percent / 100)) * 100) / 100;
    setAmountInput(String(value));
    setError(null);
    const nextRate = Number(exchangeRateInput);
    if (currencyCode && currencyCode !== getActiveCompanyCurrency().code && nextRate > 0) {
      setCurrencyAmountInput(String(value > 0 ? Math.round((value / nextRate) * 100) / 100 : ""));
    }
  };

  const save = async () => {
    if (saving) return;
    const amount = parseAmount(amountInput);
    if (amount == null) {
      setError("Enter a valid amount greater than zero.");
      return;
    }
    if (!allowExcess && outstanding > 0 && amount > outstanding + 0.009) {
      setError(`Payment amount cannot exceed ${formatCurrency(outstanding)}.`);
      return;
    }
    if (!paymentDate.trim()) {
      setError("Date is required.");
      return;
    }
    if (useReinvest && enteredAmount <= 0) {
      setError("Enter a reinvest amount greater than zero.");
      return;
    }
    if (useReinvest && enteredAmount > outstanding + 0.009) {
      setError(`Reinvest amount cannot exceed available payable of ${formatCurrency(outstanding)}.`);
      return;
    }
    if (!skipPaymentMethod && !methodDraft.paymentMethod) {
      setError("Payment method is required.");
      return;
    }
    if (useInvestorBalance && action === "receive" && investorBalance != null && amount > investorBalance + 0.009) {
      setError(`Investor Balance is insufficient. Available: ${formatCurrency(investorBalance)}.`);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const foreign = currencyCode && currencyCode !== getActiveCompanyCurrency().code;
      await onSubmit({
        amount,
        payment_date: paymentDate,
        payment_method: skipPaymentMethod ? null : (methodDraft.paymentMethod || null),
        funding_source: showFundingSource ? fundingSource : "cash_bank",
        bank_id: skipPaymentMethod ? null : (methodDraft.bankId.trim() ? Number(methodDraft.bankId) : null),
        bank_name: null,
        transaction_reference_no: skipPaymentMethod ? null : (methodDraft.transactionReferenceNo.trim() || null),
        pay_order_no: skipPaymentMethod ? null : (methodDraft.payOrderNo.trim() || null),
        cheque_no: skipPaymentMethod ? null : (methodDraft.chequeNo?.trim() || null),
        deposit_slip_no: skipPaymentMethod ? null : (methodDraft.depositSlipNo?.trim() || null),
        notes: notes.trim() || null,
        action,
        party_id: partyId,
        transaction_type: action === "receive" && productInvoiceId
          ? (transactionType || "investment")
          : (productInvoiceId ? null : transactionType),
        scheduled_payment_id: productInvoiceId ? null : scheduledPaymentId,
        product_invoice_id: productInvoiceId ?? null,
        currency_code: foreign ? currencyCode : null,
        currency_amount: foreign ? (Number(currencyAmountInput) || null) : null,
        exchange_rate: foreign ? (Number(exchangeRateInput) || null) : null,
        settlement_mode: showPayoutSettlementMode
          ? reinvestOnly
            ? "reinvest"
            : settlementMode
          : "cash_pay",
        profit_distribution_id: showPayoutSettlementMode ? Number(profitDistributionId) : null,
        destination_product_id: null,
        destination_description: useReinvest ? destinationDescription.trim() : null,
      });
      onOpenChange(false);
    } catch (err: unknown) {
      setError(productPaymentErrorMessage(err) || "An error occurred while saving the payment.");
    } finally {
      setSaving(false);
    }
  };

  const title = isEditing
    ? (isPartnerOrInvestor
        ? (isPay ? "Edit Investor / Partner Payout" : "Edit Partner Investment Receipt")
        : (isPay ? "Edit Purchase Payment" : "Edit Receipt"))
    : (isPartnerOrInvestor
        ? (isPay
            ? (reinvestOnly ? "Reinvest Investor Share" : "Record Investor / Partner Payout")
            : "Record Partner Investment Receipt")
        : (isPay ? "Record Purchase Payment" : "Record Receipt"));

  const subtitle = isEditing
    ? (isPay
        ? "Update this payable payment. Related journals and ledgers will be refreshed."
        : "Update this receivable receipt. Related journals and ledgers will be refreshed.")
    : (isPartnerOrInvestor
        ? (isPay
            ? (reinvestOnly
                ? "Reinvest the open payable into Investor Capital (310202) first. Cash can be paid later from Reserved (TBD)."
                : useReinvest
                  ? "Convert investor payable into Investor Capital (310202). Tracks destination vehicle — no cash/bank payout."
                  : "Record a cash/bank payout/settlement paid to the investor or deal partner.")
            : (useInvestorBalance
                ? "Apply Investor Capital (310202) toward this vehicle (links TBD reinvest rows). No cash/bank."
                : "Record cash/bank received from the investor against Deal Partners / Owner Payable."))
        : (isPay
            ? (isConsignment
                ? "Record a cash/bank payment for vehicle costs or charges."
                : "Record a cash/bank payment paid to the supplier for the vehicle base cost or additional costs.")
            : "Record a payment receipt received by the company."));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto p-6">
        <DialogHeader className="pb-3 border-b border-border/50">
          <div className="flex items-center gap-2">
            <div className={cn("h-9 w-9 rounded-xl border flex items-center justify-center",
              isPay ? "bg-amber-500/10 border-amber-500/20 text-amber-600" : "bg-emerald-500/10 border-emerald-500/20 text-emerald-600"
            )}>
              <HandCoins className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-foreground">{title}</DialogTitle>
              <p className="text-xs text-muted-foreground">{subtitle}</p>
            </div>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2 mt-4">
          <div className="space-y-4">
            {showPayoutSettlementMode ? (
              reinvestOnly ? (
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-foreground">Settlement Type</Label>
                  <div className="glass-input h-10 px-3 flex items-center text-sm font-medium text-foreground">
                    Reinvest into another vehicle
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-snug">
                    Dr payable · Cr Investor Capital (310202). Tracks destination vehicle. No cash payout now.
                    Cash payout is only available later from Reserved (TBD) after reinvestment.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-foreground">Settlement Type</Label>
                  <Select
                    value={settlementMode}
                    onValueChange={(v) => {
                      setSettlementMode(v as PayoutSettlementMode);
                      setError(null);
                      if (v === "reinvest") {
                        setMethodDraft(emptyPaymentMethodDraft());
                        setDestinationDescription("");
                      }
                    }}
                    disabled={saving}
                  >
                    <SelectTrigger className="glass-input h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash_pay">Cash / Bank payout</SelectItem>
                      <SelectItem value="reinvest">Reinvest into another vehicle</SelectItem>
                    </SelectContent>
                  </Select>
                  {useReinvest ? (
                    <p className="text-[11px] text-muted-foreground leading-snug">
                      Dr payable · Cr Investor Capital (310202). Tracks destination — no cash payout.
                    </p>
                  ) : (
                    <p className="text-[11px] text-muted-foreground leading-snug">
                      Dr Investor Payable · Cr Cash/Bank. Reduces investor payable.
                    </p>
                  )}
                </div>
              )
            ) : null}

            {showFundingSource ? (
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-foreground">Funding Source</Label>
                <Select
                  value={fundingSource}
                  onValueChange={(v) => {
                    const next = v as FundingSource;
                    setFundingSource(next);
                    setError(null);
                    if (next === "investor_balance") {
                      setMethodDraft(emptyPaymentMethodDraft());
                    } else {
                      setInvestorBalance(null);
                      setAmountInput(outstanding > 0 ? String(outstanding) : "");
                    }
                  }}
                  disabled={saving}
                >
                  <SelectTrigger className="glass-input h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash_bank">Cash / Bank</SelectItem>
                    <SelectItem value="investor_balance">
                      From Investor Capital (no cash)
                    </SelectItem>
                  </SelectContent>
                </Select>
                {useInvestorBalance ? (
                  <p className="text-[11px] text-muted-foreground leading-snug">
                    {investorBalanceLoading
                      ? "Loading available reinvestment…"
                      : investorBalance == null
                        ? "Could not load reinvestment balance."
                        : investorBalance <= 0.009
                          ? "No Investor Capital available to apply."
                          : `Available ${formatCurrency(investorBalance)} Investor Capital — amount filled automatically (max outstanding). Links TBD reinvest rows to this vehicle.`}
                  </p>
                ) : (
                  <p className="text-[11px] text-muted-foreground leading-snug">
                    Dr Cash/Bank · Cr Deal Partners Payable (220401).
                  </p>
                )}
              </div>
            ) : null}

            {!useReinvest ? (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="payment-currency" className="text-xs font-semibold text-foreground">
                    Payment Currency
                  </Label>
                  <Select value={currencyCode} onValueChange={handleCurrencyChange} disabled={saving}>
                    <SelectTrigger id="payment-currency" className="glass-input h-10">
                      <SelectValue placeholder={getActiveCompanyCurrency().code} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={getActiveCompanyCurrency().code}>{getActiveCompanyCurrency().code} (Base)</SelectItem>
                      <SelectItem value="JPY">JPY (Japanese Yen)</SelectItem>
                      <SelectItem value="USD">USD (US Dollar)</SelectItem>
                      <SelectItem value="AED">AED (UAE Dirham)</SelectItem>
                      <SelectItem value="GBP">GBP (British Pound)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {currencyCode && currencyCode !== getActiveCompanyCurrency().code && (
                  <div className="space-y-2">
                    <Label htmlFor="payment-exchange-rate" className="text-xs font-semibold text-foreground">
                      Exchange Rate ({getActiveCompanyCurrency().code}/{currencyCode})
                    </Label>
                    <Input
                      id="payment-exchange-rate"
                      type="number"
                      step="any"
                      className="glass-input h-10 font-semibold bg-muted/40 cursor-not-allowed text-muted-foreground"
                      value={exchangeRateInput}
                      readOnly
                      placeholder="1.0000"
                    />
                  </div>
                )}
              </div>
            ) : null}

            {!useReinvest && currencyCode && currencyCode !== getActiveCompanyCurrency().code && (
              <div className="space-y-2">
                <Label htmlFor="payment-foreign-amount" className="text-xs font-semibold text-foreground">
                  Amount in {currencyCode} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="payment-foreign-amount"
                  type="number"
                  step="any"
                  className="glass-input h-10 font-semibold"
                  value={currencyAmountInput}
                  onChange={(e) => handleForeignAmountChange(e.target.value)}
                  disabled={saving}
                  placeholder="0.00"
                />
              </div>
            )}

            <div className="space-y-2">
              <div className="flex min-h-5 items-center justify-between gap-2">
                <Label htmlFor="prod-payment-amount" className="text-xs font-semibold text-foreground">
                  {useReinvest ? "Reinvest Amount" : (isPay ? "Payment Amount" : "Receipt Amount")} ({getActiveCompanyCurrency().code}) <span className="text-destructive">*</span>
                </Label>
                {enteredAmountLabel ? (
                  <span className="shrink-0 text-xs font-bold tabular-nums text-primary">
                    {enteredAmountLabel}
                  </span>
                ) : null}
              </div>
              <div className="relative">
                <Input
                  id="prod-payment-amount"
                  className="glass-input h-10 pr-12 font-semibold"
                  value={amountInput}
                  disabled={saving}
                  onChange={(e) => {
                    handlePkrAmountChange(e.target.value);
                    setError(null);
                  }}
                  placeholder="0.00"
                  inputMode="decimal"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">
                  {getActiveCompanyCurrency().code}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5 pt-1">
                <span className="text-[10px] text-muted-foreground self-center mr-1 font-medium">Quick:</span>
                {[25, 50, 100].map((percent) => (
                  <button
                    key={percent}
                    type="button"
                    onClick={() => applyPreset(percent)}
                    className="text-[10px] font-semibold px-2 py-0.5 rounded-md border border-border/60 bg-background hover:bg-primary/10 hover:border-primary/30 hover:text-primary transition-colors"
                  >
                    {percent === 100 ? "Full" : `${percent}%`}
                  </button>
                ))}
              </div>
            </div>

            {useReinvest ? (
              <div className="space-y-2">
                <Label htmlFor="reinvest-destination-desc" className="text-xs font-semibold text-foreground">
                  Destination vehicle / description <span className="text-muted-foreground font-normal">(optional)</span>
                </Label>
                <Textarea
                  id="reinvest-destination-desc"
                  className="glass-input min-h-[80px]"
                  value={destinationDescription}
                  onChange={(e) => {
                    setDestinationDescription(e.target.value);
                    setError(null);
                  }}
                  disabled={saving}
                  placeholder="Optional — leave blank if the next vehicle is not decided yet…"
                />
                <p className="text-[11px] text-muted-foreground leading-snug">
                  Amount alone is enough. Add a note later when the car is known.
                </p>
              </div>
            ) : null}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-foreground">Date</Label>
                <DatePartsPicker value={paymentDate} onChange={setPaymentDate} disabled={saving} />
              </div>

              {!skipPaymentMethod ? (
                <PaymentMethodFieldsGroup
                  id="prod-payment-method"
                  value={methodDraft}
                  onChange={setMethodDraft}
                  disabled={saving}
                  showDetails={false}
                  labelClassName="text-xs font-semibold text-foreground"
                />
              ) : (
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-foreground">Payment Method</Label>
                  <div className="h-10 rounded-md border border-border/60 bg-muted/30 px-3 flex items-center text-xs text-muted-foreground">
                    {useReinvest ? "Not required (reinvest)" : "Not required (balance adjustment)"}
                  </div>
                </div>
              )}
            </div>
            {!skipPaymentMethod ? (
              <PaymentMethodFieldsGroup
                value={methodDraft}
                onChange={setMethodDraft}
                disabled={saving}
                showSelect={false}
              />
            ) : null}

            <div className="space-y-2">
              <Label htmlFor="prod-payment-notes" className="text-xs font-semibold text-foreground">Notes</Label>
              <Textarea
                id="prod-payment-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes"
                rows={2}
                disabled={saving}
              />
            </div>

            {error ? <p className="text-sm text-destructive">{error}</p> : null}
          </div>

          <div className="flex flex-col justify-between rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-4">
            {invoiceNo ? (
              <div>
                <div className="flex items-center gap-2 pb-3 border-b border-primary/10">
                  <FileText className="w-4 h-4 text-primary" />
                  <div>
                    <p className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider">Invoice</p>
                    <p className="text-sm font-bold text-foreground">{invoiceNo}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 pt-3 text-[11px]">
                  <div>
                    <p className="text-muted-foreground">Invoice Date</p>
                    <p className="font-semibold text-foreground">{invoiceDate || "—"}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Due Date</p>
                    <p className="font-semibold text-foreground">{invoiceDueDate || "—"}</p>
                  </div>
                </div>
                {invoiceCostItems && invoiceCostItems.length > 0 ? (
                  <div className="mt-3 space-y-1.5">
                    <p className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider">Cost items</p>
                    {invoiceCostItems.map((item, idx) => (
                      <div key={idx} className="flex justify-between gap-2 text-[11px]">
                        <span className="text-muted-foreground truncate">{item.label}</span>
                        <span className="font-semibold tabular-nums text-foreground shrink-0">
                          {formatCurrency(item.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="flex items-start gap-2">
                <Calculator className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs font-bold text-foreground">
                    {useReinvest ? "Reinvestment summary" : "Payment summary"}
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    {useReinvest
                      ? "Clears payable into Investor Capital (310202) and tracks destination vehicle."
                      : useInvestorBalance
                        ? "Live Investor Capital (310202) draw and remaining balance."
                        : "Live impact against the outstanding balance."}
                  </p>
                </div>
              </div>
            )}

            <div>
              <div className="space-y-2 text-xs">
                {(partyName?.trim() || lineLabel?.trim() || invoiceNo) ? (
                  <div className="rounded-lg border border-primary/15 bg-background/60 px-2.5 py-2 space-y-1.5">
                    <p className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">
                      {isPay ? "Paying toward" : "Receiving against"}
                    </p>
                    {partyName?.trim() ? (
                      <div className="flex justify-between items-start gap-3">
                        <span className="text-muted-foreground">Party</span>
                        <span className="font-semibold text-foreground text-right">{partyName.trim()}</span>
                      </div>
                    ) : null}
                    {(lineLabel?.trim() || invoiceNo) ? (
                      <div className="flex justify-between items-start gap-3">
                        <span className="text-muted-foreground">{isPay ? "Payable" : "Receivable"}</span>
                        <span className="font-semibold text-foreground text-right">
                          {lineLabel?.trim() || (invoiceNo ? `Invoice ${invoiceNo}` : "—")}
                        </span>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                <div className="flex justify-between items-start gap-4">
                  <span className="text-muted-foreground">Outstanding</span>
                  <span className="font-semibold tabular-nums text-foreground">{formatCurrency(outstanding)}</span>
                </div>
                <div className="flex justify-between items-start gap-4">
                  <span className="text-muted-foreground">{useReinvest ? "Reinvesting" : (isPay ? "Paying" : "Receiving")}</span>
                  <div className="min-w-0 text-right">
                    <span className={cn("font-bold tabular-nums block", isPay || useReinvest ? "text-rose-600" : "text-emerald-600")}>
                      {isPay || useReinvest ? "-" : "+"} {formatCurrency(enteredAmount)}
                    </span>
                    {hasForeign && enteredForeign > 0 && !useReinvest && (
                      <span className={cn("text-[10px] block font-medium tabular-nums mt-0.5", isPay ? "text-rose-600/80" : "text-emerald-600/80")}>
                        {isPay ? "-" : "+"} {formatCurrency(enteredForeign, currencyCode)} @ {rate}
                      </span>
                    )}
                  </div>
                </div>

                {useInvestorBalance ? (
                  <div className="rounded-lg border border-primary/15 bg-background/60 px-2.5 py-2 space-y-1.5">
                    <p className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">
                      Investor Capital (310202)
                    </p>
                    {investorBalanceLoading ? (
                      <p className="text-[11px] text-muted-foreground">Loading available capital…</p>
                    ) : investorCapitalAvailable == null ? (
                      <p className="text-[11px] text-destructive">Could not load Investor Capital balance.</p>
                    ) : (
                      <>
                        <div className="flex justify-between gap-3">
                          <span className="text-muted-foreground">Available</span>
                          <span className="font-semibold tabular-nums text-foreground">
                            {formatCurrency(investorCapitalAvailable)}
                          </span>
                        </div>
                        <div className="flex justify-between gap-3">
                          <span className="text-muted-foreground">Receiving</span>
                          <span className="font-bold tabular-nums text-emerald-600">
                            − {formatCurrency(enteredAmount)}
                          </span>
                        </div>
                        <div className="flex justify-between gap-3 border-t border-primary/10 pt-1.5">
                          <span className="text-muted-foreground">Remaining capital</span>
                          <span
                            className={cn(
                              "font-extrabold tabular-nums text-sm",
                              enteredAmount > investorCapitalAvailable + 0.009
                                ? "text-destructive"
                                : "text-primary",
                            )}
                          >
                            {formatCurrency(investorCapitalRemaining ?? 0)}
                          </span>
                        </div>
                        {enteredAmount > investorCapitalAvailable + 0.009 ? (
                          <p className="text-[10px] text-destructive leading-snug">
                            Receiving exceeds available Investor Capital.
                          </p>
                        ) : null}
                      </>
                    )}
                  </div>
                ) : null}

                {allowExcess && excessAmount > 0.009 ? (
                  <div className="rounded-lg border border-amber-500/25 bg-amber-500/10 px-2.5 py-2 space-y-1">
                    <div className="flex justify-between gap-3 text-[11px]">
                      <span className="text-muted-foreground">Applied to line</span>
                      <span className="font-semibold tabular-nums text-foreground">{formatCurrency(appliedAmount)}</span>
                    </div>
                    <div className="flex justify-between gap-3 text-[11px]">
                      <span className="text-muted-foreground">Extra / profit</span>
                      <span className="font-semibold tabular-nums text-amber-700 dark:text-amber-400">{formatCurrency(excessAmount)}</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground leading-snug">
                      Only {formatCurrency(appliedAmount)} settles this receivable. The excess {formatCurrency(excessAmount)} is saved in extra payments as profit.
                    </p>
                  </div>
                ) : null}

                {!allowExcess && enteredAmount > outstanding + 0.009 ? (
                  <p className="text-[11px] text-destructive leading-snug">
                    Payment cannot exceed outstanding {formatCurrency(outstanding)}.
                  </p>
                ) : null}

                <div className="flex justify-between items-start border-t border-primary/10 pt-2 gap-4">
                  <span className="text-muted-foreground">
                    Remaining Balance
                  </span>
                  <div className="min-w-0 text-right">
                    <span className="font-extrabold text-primary text-sm block">
                      {formatCurrency(remaining)}
                    </span>
                    {hasForeign && !useReinvest && (
                      <span className="text-[10px] text-primary/80 block font-medium tabular-nums mt-0.5">
                        {formatCurrency(remainingForeign, currencyCode)}
                      </span>
                    )}
                  </div>
                </div>

                {enteredAmount > 0 ? (
                  <div className="bg-background/80 rounded-lg border border-primary/20 p-3 space-y-2 mt-2">
                    <div className="flex justify-between text-[11px] font-bold">
                      <span className="text-muted-foreground">Transaction Progress</span>
                      <span className="text-primary">{paidPercent}%</span>
                    </div>
                    <div className="h-2 w-full bg-primary/20 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full"
                        style={{ width: `${paidPercent}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-muted-foreground italic text-center pt-0.5">
                      {useReinvest
                        ? `This will clear ${paidPercent}% of the available payable (reinvest).`
                        : `This will settle ${paidPercent}% of the outstanding balance.`}
                    </p>
                  </div>
                ) : (
                  <div className="bg-background/50 rounded-lg border border-border/50 p-3 text-center text-xs text-muted-foreground italic">
                    Enter transaction amount to view live balance impact.
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground pt-2 border-t border-primary/10">
              <Info className="w-3.5 h-3.5 text-primary shrink-0" />
              <span>
                {useReinvest
                  ? "Reinvest posts Dr payable → Cr Investor Capital (310202) and tracks the destination vehicle."
                  : useInvestorBalance
                    ? "Draws Investor Capital (310202) onto this vehicle’s payable. Cash/Bank is not moved."
                    : "General ledger journals and cash/bank accounts are adjusted automatically upon submission."}
              </span>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4 border-t border-border/50 pt-3">
          <Button type="button" variant="glass" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" variant="premium" onClick={() => void save()} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              useReinvest
                ? "Save Reinvest"
                : (isEditing
                    ? (isPay ? "Update Payment" : "Update Receipt")
                    : (isPay ? "Record Payment" : "Record Receipt"))
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
